import React from 'react';
import propTypes from 'prop-types';
import { cn } from 'lib/utils';

/**
 * labeled multiline field with an inline error message, styled to match TextField
 * @param label - question shown above the textarea
 * @param name - field name, also used as its id
 * @param value - current value
 * @param onChange - called with the new value
 * @param error - error message shown under the field, empty when the value is valid
 * @param placeholder - hint about the expected content
 * @param required - whether to show the required asterisk next to the label
 * @param rows - visible line count
 * @param maxLength - hard limit of characters, also drives the counter
 * @param showCounter - renders the used/max characters next to the label
 * @param autoFocus - focuses the textarea on mount
 * @param className - extra classes for the wrapper
 */
export default function TextArea({
  label,
  name,
  value,
  onChange,
  error = '',
  placeholder,
  required = true,
  rows = 4,
  maxLength,
  showCounter = false,
  autoFocus = false,
  className = ''
}) {
  // a saved record can come back with a null description; keep the field controlled
  const text = value ?? '';

  return (
    <div className={cn('flex min-w-0 flex-col', className)}>
      <div className='mb-1.5 flex items-baseline justify-between gap-2'>
        <label className='text-sm font-semibold text-black' htmlFor={name}>
          {label}
          {required && <span className='text-semantic-danger'> *</span>}
        </label>
        {showCounter && maxLength && (
          <span className='shrink-0 text-[11px] tabular-nums text-grey-light'>
            {text.length}/{maxLength}
          </span>
        )}
      </div>
      <textarea
        id={name}
        name={name}
        rows={rows}
        value={text}
        maxLength={maxLength}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={e => onChange(e.target.value)}
        className={cn(
          'w-full resize-none rounded-lg border border-grey-lighter bg-white px-3 py-2 text-black',
          'text-[length:--font-size-body] outline-none placeholder:text-grey-light focus:border-primary-emphasis',
          error && 'border-semantic-danger focus:border-semantic-danger'
        )}
      />
      {error && (
        <p className='mt-1 mb-0 text-xs leading-tight text-semantic-danger'>
          {error}
        </p>
      )}
    </div>
  );
}

TextArea.propTypes = {
  label: propTypes.string.isRequired,
  name: propTypes.string.isRequired,
  value: propTypes.string,
  onChange: propTypes.func.isRequired,
  error: propTypes.string,
  placeholder: propTypes.string,
  required: propTypes.bool,
  rows: propTypes.number,
  maxLength: propTypes.number,
  showCounter: propTypes.bool,
  autoFocus: propTypes.bool,
  className: propTypes.string
};
